import { useState, useEffect, useCallback } from 'react';
import { fetchHoliday, HolidayRecord, HolidayResponse, payOutHoliday, PayOutResponse , getBalanceHoliday, HolidayPayResponse } from '../api/holidaypayreport/index';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';


interface UseHolidayRecordsResult {
  getHolidayAmount: (fromDate?: string, toDate?: string) => Promise<void>;
  calculatedBalance: HolidayPayResponse;
  payoutAmount: (amount: number) => Promise<void>;
  fetchData: (page: number , fromDate?: string, toDate?: string, status?: string) => Promise<void>;
  holidayRecords: HolidayRecord[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  searchQuery: string;
  pageSize: number;
  paginatedHolidayRecords: HolidayRecord[];
  setCurrentPage: (page: number) => void;
  setSearchQuery: (query: string) => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  refetch: () => Promise<void>;
}

export const useHolidayRecords = (pageSize: number = 10): UseHolidayRecordsResult => {
  const [holidayRecords, setHolidayRecords] = useState<HolidayRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [calculatedBalance, setCalculatedBalance] = useState<HolidayPayResponse>({} as HolidayPayResponse);
  const { t } = useTranslation();
  const fetchData = useCallback(async (currentPage: number , fromDate?: string, toDate?: string, status?: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        PageNumber: currentPage,
        PageSize: pageSize,
      };
      
      if (fromDate) {
        params.fromDate = fromDate;
      }
      if (toDate) {
        params.toDate = toDate;
      }
      if (status) {
        params.status = status;
      }
      
      const response: HolidayResponse = await fetchHoliday(params);
      if (response && response.response) {
        setHolidayRecords(response.response);
      }
    } catch (err) {
      console.error('Failed to fetch holiday records:', err);
      setError((err as Error).message || 'Failed to load holiday records');
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  const payoutAmount = useCallback(async (amount: number) => {
    try {
      setLoading(true);
      setError(null);
      const response: PayOutResponse = await payOutHoliday(amount);
      if (response.statusCode === 200) {
        toast.success(t('PAYOUT_SUCCESS') as string);
      }
    } catch (err) {
      console.error('Failed to payout holiday amount:', err);
      setError((err as Error).message || 'Failed to payout holiday amount');
    } finally {
      setLoading(false);
    }

  }, [pageSize]);

  const getHolidayAmount = useCallback(async (fromDate?: string, toDate?: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getBalanceHoliday(fromDate, toDate);
      if(response.statusCode == 200){
        setCalculatedBalance(response);
      }
    } catch (err) {
      console.error('Failed to fetch holiday pension:', err);
      setError((err as Error).message || 'Failed to load holiday pension');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, []);
  useEffect(() => {
    getHolidayAmount();
  }, [currentPage]);

  const filteredHolidayRecords = holidayRecords.filter((record) => {
    const matchesSearch = 
      record.amountToPay.toString().includes(searchQuery) ||
      record.taxAmount.toString().includes(searchQuery) ||
      record.id.toString().includes(searchQuery);
    
    return matchesSearch;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredHolidayRecords.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedHolidayRecords = filteredHolidayRecords.slice(startIndex, startIndex + pageSize);

  // Navigation handlers
  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, totalPages]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);

  // Refetch function
  const refetch = useCallback(async () => {
    await fetchData(currentPage);
  }, [currentPage, fetchData]);

  return {
    getHolidayAmount,
    calculatedBalance, 
    payoutAmount,
    fetchData,
    holidayRecords,
    loading,
    error,
    currentPage,
    totalPages,
    searchQuery,
    pageSize,
    paginatedHolidayRecords,
    setCurrentPage,
    setSearchQuery,
    goToNextPage,
    goToPreviousPage,
    refetch,
  };
};
